import { type CreateExportInput, type ExportFormat, type ExportJob, exportKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'

const POLL_INTERVAL_MS = 2000

export interface UseExportsOptions {
  readonly ledgerId: () => string | undefined
  /** The screen never issues a request for a capability the ledger did not grant. */
  readonly enabled: () => boolean
  /** CSV and BACKUP jobs share one list endpoint; each screen only ever shows its own kind. */
  readonly format: ExportFormat
}

export interface UseExports {
  readonly jobs: ComputedRef<readonly ExportJob[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly createError: ComputedRef<Error | null>
  readonly downloadingId: ComputedRef<string | undefined>
  readonly downloadError: ComputedRef<Error | null>
  retry(): void
  create(input: CreateExportInput): void
  download(job: ExportJob): void
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function isWatching(jobs: readonly ExportJob[] | undefined, format: ExportFormat): boolean {
  return (jobs ?? []).some(
    (job) => job.format === format && (job.status === 'QUEUED' || job.status === 'RUNNING'),
  )
}

export function useExports(options: UseExportsOptions): UseExports {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const downloadingId = ref<string | undefined>(undefined)

  const listQuery = useQuery({
    queryKey: computed(() => exportKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.exports.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
    refetchInterval: (query) => (isWatching(query.state.data, options.format) ? POLL_INTERVAL_MS : false),
  })

  const jobs = computed(() => (listQuery.data.value ?? []).filter((job) => job.format === options.format))

  const createMutation = useMutation({
    mutationFn: (input: CreateExportInput) => api.exports.create(options.ledgerId()!, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exportKeys.list(options.ledgerId() ?? '') })
    },
  })

  const downloadMutation = useMutation({
    mutationFn: async (job: ExportJob) => {
      downloadingId.value = job.id
      return api.exports.download(options.ledgerId()!, job.id)
    },
    onSuccess: (download) => saveBlob(download.blob, download.filename),
    onSettled: () => {
      downloadingId.value = undefined
    },
  })

  return {
    jobs,
    isLoading: computed(() => active.value && listQuery.isPending.value),
    error: computed(() => listQuery.error.value),
    retrying: retrying(listQuery),
    isCreating: computed(() => createMutation.isPending.value),
    createError: computed(() => createMutation.error.value),
    downloadingId: computed(() => downloadingId.value),
    downloadError: computed(() => downloadMutation.error.value),
    retry: () => {
      void listQuery.refetch()
    },
    create: (input) => createMutation.mutate(input),
    download: (job) => downloadMutation.mutate(job),
  }
}
